import { ErrorCode } from '@ps/web-core-enums';
import { StarsButton as Button, NumericPassword, Translation } from '@ps/web-shared-components';
import { StarsInfo, createForm } from '@ps/web-shared-ui';
import type { FormApi } from 'final-form';
import React from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import ButtonGroup from '@swf/components/ButtonGroup/ButtonGroup';
import useSearchParams from '@swf/core/hooks/useSearchParams';
import type { LoginPayload } from '@swf/core/models/auth/login';
import type { Dispatch } from '@swf/core/store';
import { select } from '@swf/core/store';

import {
  TXTCLI_Login,
  TXTCLI_Please_enter_your_RSA_Passcode_ELL,
  TXTCLI_RSA_Passcode,
  TXTCLI_You_are_already_logged_in_from_ELL,
  TXTWCL_Enter_Numbers,
  TXTWCL_The_RSA_Passcode_you_have_ente_ELL
} from './RSAForm.messages.json';
import styles from './RSAForm.scss';

interface FormValues {
  rsaCode?: string;
}

const Form = createForm<FormValues>();

const RSAForm: React.FC = () => {
  const intl = useIntl();
  const searchParams = useSearchParams<LoginPayload>();
  const { errorCode } = useSelector(select.auth.getResponse);

  const { auth } = useDispatch<Dispatch>();

  const handleSubmit = async ({ rsaCode }: FormValues, formApi: FormApi<FormValues>) => {
    formApi.initialize({});
    await auth.login({ rsa: rsaCode, ...searchParams });
  };

  return (
    <Form
      onSubmit={handleSubmit}
      className={styles.form}
      render={({ pristine, submitting, dirtySinceLastSubmit }) => (
        <>
          {errorCode && dirtySinceLastSubmit && !submitting && (
            <StarsInfo className={styles.errorMessage} type="error">
              {errorCode === ErrorCode.APC_ON_DIFF_DEVICE ? (
                <Translation {...TXTCLI_You_are_already_logged_in_from_ELL} />
              ) : (
                <Translation {...TXTWCL_The_RSA_Passcode_you_have_ente_ELL} />
              )}
            </StarsInfo>
          )}
          <Translation tagName="p" {...TXTCLI_Please_enter_your_RSA_Passcode_ELL} />
          <NumericPassword
            id="rsaCode"
            label={TXTCLI_RSA_Passcode}
            placeholder={intl.formatMessage(TXTWCL_Enter_Numbers)}
          />
          <ButtonGroup className={styles.buttonGroup}>
            <Button
              block
              type="submit"
              disabled={pristine || submitting}
              loading={submitting}
              translation={TXTCLI_Login}
            />
          </ButtonGroup>
        </>
      )}
    />
  );
};

export default RSAForm;
